import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const { login, demoLogin, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/vacancies', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password.trim()) {
      setError('Введите email и пароль');
      return;
    }
    setIsLoading(true);
    try {
      await login(email.trim(), password.trim());
      navigate('/vacancies', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка входа';
      if (msg.includes('fetch') || msg.includes('network') || msg.includes('Failed')) {
        setError('Не удалось подключиться к серверу. Возможно, ваш бэкенд не разрешает CORS-запросы с этого домена. Используйте «Демо-режим» ниже, чтобы посмотреть платформу.');
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setIsDemoLoading(true);
    try {
      await demoLogin();
      navigate('/vacancies', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка демо-режима');
    } finally {
      setIsDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side — Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-background-100 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20geometric%20shapes%20representing%20neural%20networks%20and%20data%20analytics%2C%20soft%20muted%20warm%20tones%20beige%20cream%20and%20light%20sage%20green%2C%20flowing%20organic%20curves%20intersecting%20with%20precise%20grid%20lines%2C%20subtle%20gradient%20background%2C%20corporate%20technology%20aesthetic%2C%20clean%20modern%20illustration%2C%20no%20text%2C%20editorial%20style%2C%20warm%20neutral%20palette&width=800&height=900&seq=login-hero-01&orientation=portrait"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20"></div>
        <div className="relative z-10 flex flex-col justify-between p-10 h-full">
          <p className="text-sm font-medium text-foreground-700 tracking-wide">
            AI-Powered Recruitment Platform
          </p>
          <div>
            <h2 className="font-heading font-bold text-foreground-950 leading-[1.1]" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
              Находите<br />
              лучших<br />
              кандидатов
            </h2>
            <p className="mt-4 text-foreground-600 text-sm max-w-xs leading-relaxed">
              AI-скоринг резюме, анкет и совместимости с руководителем — всё в одной платформе
            </p>
          </div>
        </div>
      </div>

      {/* Right side — Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-background-50 px-6 py-12">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-10 justify-center">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <span className="text-white font-heading font-bold text-sm">H</span>
            </div>
            <div>
              <span className="font-heading font-bold text-foreground-950 text-base">HR.Starec</span>
              <p className="text-[11px] text-foreground-400 leading-tight">AI-платформа подбора</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-background-200 p-8 md:p-10">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-1">
              Вход в HR.Starec
            </h1>
            <p className="text-sm text-foreground-500 text-center mb-8">
              Введите данные для входа в систему
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Пароль
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Введите пароль"
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-6 rounded-xl bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 active:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Вход...
                  </>
                ) : (
                  <>
                    Войти в систему
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-background-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-white text-xs text-foreground-400">или</span>
              </div>
            </div>

            <button
              onClick={handleDemoLogin}
              disabled={isDemoLoading}
              className="w-full py-3 px-6 rounded-xl bg-accent-500 text-white font-medium text-sm hover:bg-accent-600 active:bg-accent-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              {isDemoLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Загрузка...
                </>
              ) : (
                <>
                  <i className="ri-rocket-line"></i>
                  Войти в демо-режим
                </>
              )}
            </button>

            <p className="text-center text-xs text-foreground-400 mt-3">
              Демо-режим — посмотреть платформу без подключения к бэкенду
            </p>

          </div>

          <p className="text-center text-xs text-foreground-400 mt-6">
            HR.Starec — AI-платформа подбора персонала
          </p>
        </div>
      </div>
    </div>
  );
}